import { App } from '@capacitor/app'
import type { PluginListenerHandle } from '@capacitor/core'

/**
 * CallKit → in-app Active Call deep links.
 *
 * Native (`AscendCallManager.bringAppToForeground`) opens
 * `ascendcomms://callkit/answered?callId=…&uuid=…` after a CallKit answer /
 * unlock so Capacitor can foreground the WebView. Dynamic Island / green-pill
 * taps usually just activate the app (no URL); those are handled via the
 * `callKitReturn` native event + visibility listeners in the voice-call
 * controller. This module covers the URL path so `appUrlOpen` is not a no-op
 * (OAuth previously owned the only `appUrlOpen` listener).
 */

const NATIVE_CALLKIT_ANSWERED_PREFIX = 'ascendcomms://callkit/answered'

export type CallKitAnsweredDeepLink = {
  callId?: string
  uuid?: string
}

type CallKitDeepLinkHandler = (link: CallKitAnsweredDeepLink) => void

let listener: PluginListenerHandle | null = null
const handlers = new Set<CallKitDeepLinkHandler>()

function isNativeCallKitAnsweredUrl(url: string): boolean {
  return url.startsWith(NATIVE_CALLKIT_ANSWERED_PREFIX)
}

function parseCallKitAnsweredUrl(url: string): CallKitAnsweredDeepLink | null {
  if (!isNativeCallKitAnsweredUrl(url)) return null
  try {
    const parsed = new URL(url)
    const callId = parsed.searchParams.get('callId')?.trim() || undefined
    const uuid = parsed.searchParams.get('uuid')?.trim() || undefined
    return { callId, uuid }
  } catch {
    // Some Capacitor builds deliver the scheme without a parseable host; fall
    // back to a loose query parse so we still extract callId.
    const q = url.indexOf('?')
    if (q < 0) return {}
    const params = new URLSearchParams(url.slice(q + 1))
    return {
      callId: params.get('callId')?.trim() || undefined,
      uuid: params.get('uuid')?.trim() || undefined,
    }
  }
}

function dispatch(url: string): void {
  const link = parseCallKitAnsweredUrl(url)
  if (!link) return
  for (const handler of handlers) {
    try {
      handler(link)
    } catch {
      /* handler errors must not break other listeners */
    }
  }
}

/** Register once at startup (alongside OAuth). Safe to call multiple times. */
export function registerNativeCallDeepLinkHandlers(): void {
  if (listener) return

  void App.addListener('appUrlOpen', (event) => {
    if (event?.url) dispatch(event.url)
  }).then((handle) => {
    listener = handle
  })

  void App.getLaunchUrl().then((launch) => {
    if (launch?.url) dispatch(launch.url)
  })
}

/**
 * Subscribe to CallKit answered deep links. Returns unsubscribe.
 * Handlers run for warm `appUrlOpen` and cold-start launch URL.
 */
export function addCallKitAnsweredDeepLinkListener(
  handler: CallKitDeepLinkHandler,
): () => void {
  handlers.add(handler)
  return () => {
    handlers.delete(handler)
  }
}
